import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import SearchSelect from "./SearchSelect";
import { loadFromSupabase, saveToSupabase, deleteFromSupabase } from "../supabaseDB";

var T = {
  en: {
    title: "Fee Catalog", subtitle: "Manage expense types for Bills & Invoices",
    add: "New fee type", edit: "Edit fee type",
    name: "Fee Name", nameEs: "Name (ES)", nameEn: "Name (EN)",
    defaultRate: "Default Rate", unit: "Unit",
    unitPallet: "Per pallet", unitCase: "Per case", unitFlat: "Flat rate", unitPercent: "% of sales",
    appliesTo: "Applies to", appBill: "Bill (to shipper)", appInvoice: "Invoice (to client)", appBoth: "Both",
    markupRate: "Markup Rate", markupSub: "What you charge the shipper",
    costRate: "Actual Cost", costSub: "What you actually pay",
    category: "Category", catCustoms: "Customs", catWarehouse: "Warehouse", catQC: "Quality/Inspection",
    catShipping: "Shipping", catFinancing: "Financing", catCertification: "Certification", catOther: "Other",
    status: "Status", active: "Active", inactive: "Inactive",
    showInactive: "Show inactive",
    save: "Save", cancel: "Cancel", delete: "Delete", editBtn: "Edit", close: "Close",
    confirmDel: "Delete this fee type?", yes: "Yes, delete", no: "Cancel",
    saved: "Fee type saved", deleted: "Fee type deleted",
    showing: "Showing", of: "of", total: "fee types",
    noResults: "No fee types found",
    search: "Search by name...",
    allCategories: "All categories",
    profitLoss: "Profit/Loss", marginOnFee: "Margin",
    addCategory: "Add category",
    manageCategories: "Manage categories", manageCategoriesTitle: "Categories",
    builtIn: "Built-in", custom: "Custom",
    newCategoryPlaceholder: "New category name", addBtn: "Add",
    categoryInUse: "{n} fee type(s) using this category will be moved to \"Other\".",
    confirmDeleteCategory: "Delete this category?",
    categoryExists: "That category already exists",
    noCustomCategories: "No custom categories yet — add one below.",
  },
  es: {
    title: "Catálogo de Gastos", subtitle: "Administra tipos de gasto para Bills y Facturas",
    add: "Nuevo tipo de gasto", edit: "Editar tipo de gasto",
    name: "Nombre del Gasto", nameEs: "Nombre (ES)", nameEn: "Nombre (EN)",
    defaultRate: "Tarifa Default", unit: "Unidad",
    unitPallet: "Por pallet", unitCase: "Por caja", unitFlat: "Tarifa fija", unitPercent: "% de ventas",
    appliesTo: "Aplica a", appBill: "Bill (al proveedor)", appInvoice: "Factura (al cliente)", appBoth: "Ambos",
    markupRate: "Tarifa de Cobro", markupSub: "Lo que cobras al proveedor",
    costRate: "Costo Real", costSub: "Lo que realmente pagas",
    category: "Categoría", catCustoms: "Aduana", catWarehouse: "Bodega", catQC: "Calidad/Inspección",
    catShipping: "Envío", catFinancing: "Financiamiento", catCertification: "Certificación", catOther: "Otro",
    status: "Estado", active: "Activo", inactive: "Inactivo",
    showInactive: "Mostrar inactivos",
    save: "Guardar", cancel: "Cancelar", delete: "Eliminar", editBtn: "Editar", close: "Cerrar",
    confirmDel: "¿Eliminar este tipo de gasto?", yes: "Sí, eliminar", no: "Cancelar",
    saved: "Tipo de gasto guardado", deleted: "Tipo de gasto eliminado",
    showing: "Mostrando", of: "de", total: "tipos de gasto",
    noResults: "Sin tipos de gasto",
    search: "Buscar por nombre...",
    allCategories: "Todas las categorías",
    profitLoss: "Ganancia/Pérdida", marginOnFee: "Margen",
    addCategory: "Agregar categoría",
    manageCategories: "Administrar categorías", manageCategoriesTitle: "Categorías",
    builtIn: "Predefinida", custom: "Personalizada",
    newCategoryPlaceholder: "Nombre de la nueva categoría", addBtn: "Agregar",
    categoryInUse: "{n} tipo(s) de gasto con esta categoría se moverán a \"Otro\".",
    confirmDeleteCategory: "¿Eliminar esta categoría?",
    categoryExists: "Esa categoría ya existe",
    noCustomCategories: "Aún no hay categorías personalizadas — agrega una abajo.",
  },
};

var CATEGORIES = ["customs", "warehouse", "qc", "shipping", "financing", "certification", "other"];
var UNITS = ["pallet", "case", "flat", "percent"];
var APPLIES = ["bill", "invoice", "both"];
/* Arturo, 2026-09-16: "lo ideal seria que yo pudiera agregar las
   categorias manualmente" — fixed CATEGORIES stay first-class
   (translated labels via catKeys), plus any custom ones the user
   types, labeled with the raw string since there's no translation for
   them. 2026-09-28 (Arturo: "necesito yo mismo sin codigo poder crear
   o eliminar una categoria, no se como estan generadas") — custom
   categories moved from a localStorage-only key (never synced across
   devices, no way to delete one) to the `fee_categories` Supabase
   table, managed from a real "Manage categories" screen instead of
   only being creatable inline while editing a single fee. The old
   local key is read once, below, purely to migrate anything Arturo
   already typed there before this feature existed — never written to
   again after that. */
var CAT_STORAGE = "gfi-fee-catalog-categories";
function loadLegacyLocalCats() { try { var d = localStorage.getItem(CAT_STORAGE); return d ? JSON.parse(d) : []; } catch (e) { return []; } }
var inp = { padding: "7px 8px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

var DEFAULT_FEES = [
  { id: "FEE-001", name: "Custom Broker Fee", category: "customs", unit: "flat", markupRate: 75, costRate: 75, appliesTo: "bill", isActive: true },
  { id: "FEE-002", name: "Warehouse Fees", category: "warehouse", unit: "pallet", markupRate: 24, costRate: 18, appliesTo: "both", isActive: true },
  { id: "FEE-003", name: "QC Inspection", category: "qc", unit: "pallet", markupRate: 14, costRate: 12, appliesTo: "both", isActive: true },
  { id: "FEE-004", name: "Temp Recorders", category: "other", unit: "flat", markupRate: 35, costRate: 35, appliesTo: "bill", isActive: true },
  { id: "FEE-005", name: "Advance Financing Fee", category: "financing", unit: "percent", markupRate: 3, costRate: 2, appliesTo: "bill", isActive: true },
  { id: "FEE-006", name: "Wire Transfer Fee", category: "other", unit: "flat", markupRate: 45, costRate: 45, appliesTo: "bill", isActive: true },
  { id: "FEE-007", name: "Shipping", category: "shipping", unit: "flat", markupRate: 0, costRate: 0, appliesTo: "bill", isActive: true },
  { id: "FEE-008", name: "Repack", category: "warehouse", unit: "case", markupRate: 0, costRate: 0, appliesTo: "bill", isActive: true },
  { id: "FEE-009", name: "Fumigation", category: "qc", unit: "flat", markupRate: 0, costRate: 0, appliesTo: "bill", isActive: true },
  { id: "FEE-010", name: "USDA Inspection", category: "qc", unit: "flat", markupRate: 0, costRate: 0, appliesTo: "bill", isActive: true },
];

function nextId(items) { var nums = items.map(function(i) { return parseInt((i.id || "").replace("FEE-", ""), 10); }).filter(function(n) { return !isNaN(n); }); return "FEE-" + String((Math.max(0, ...nums) + 1)).padStart(3, "0"); }
function fmtMoney(n) { return "$" + (parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(function() {
    var fadeT = setTimeout(function() { setFading(true); }, 2700);
    var closeT = setTimeout(function() { onClose(); }, 3000);
    return function() { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 24px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

function FeeForm({ fee, fees, t, lang, customCats, onAddCategory, onSave, onDelete, onCancel }) {
  var isNew = !fee;
  var catKeys = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", financing: "catFinancing", certification: "catCertification", other: "catOther" };
  var unitKeys = { pallet: "unitPallet", case: "unitCase", flat: "unitFlat", percent: "unitPercent" };
  var appKeys = { bill: "appBill", invoice: "appInvoice", both: "appBoth" };

  var [f, setF] = useState(fee ? { ...fee } : { id: "", name: "", category: "other", unit: "flat", markupRate: "", costRate: "", appliesTo: "bill", isActive: true });
  var [errors, setErrors] = useState({});
  var [showDel, setShowDel] = useState(false);
  var [showNewCat, setShowNewCat] = useState(false);
  var [newCat, setNewCat] = useState("");
  var customCatNames = (customCats || []).map(function(c) { return c.name; });
  var allCats = CATEGORIES.concat(customCatNames.filter(function(c) { return !CATEGORIES.includes(c); }));

  var set = function(k, v) { setF(function(p) { return { ...p, [k]: v }; }); };
  var margin = (parseFloat(f.markupRate) || 0) - (parseFloat(f.costRate) || 0);

  var handleSave = function() {
    var errs = {};
    /* Supabase returns null for empty text — guard against
       "Cannot read properties of null (reading 'trim')". */
    var name = f.name == null ? "" : String(f.name);
    if (!name.trim()) errs.name = true;
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onSave({ ...f, name: name, id: f.id || nextId(fees) });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }} onClick={onCancel}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ width: 500, maxWidth: "95vw", background: "var(--color-bg-card-solid)", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{isNew ? t.add : t.edit}</span>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 16 }}>{"\u2715"}</button>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.name}*</div><input value={f.name} onChange={function(e) { set("name", e.target.value); }} style={{ ...inp, ...(errors.name ? { borderColor: "#ef4444" } : {}) }} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.category}</div>
              <SearchSelect value={f.category} onChange={function(v) { if (v === "__new__") setShowNewCat(true); else set("category", v); }} options={[...allCats.map(function(c) { return { value: c, label: t[catKeys[c]] || c }; }), { value: "__new__", label: "+ " + t.addCategory }]} />
              {showNewCat && <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                <input value={newCat} onChange={function(e) { setNewCat(e.target.value); }} style={{ ...inp, borderStyle: "dashed", flex: 1, fontSize: 10 }} />
                <button onClick={async function() {
                  var v = newCat.trim();
                  if (!v) return;
                  if (!allCats.includes(v)) await onAddCategory(v);
                  set("category", v); setNewCat(""); setShowNewCat(false);
                }} style={{ padding: "4px 8px", borderRadius: 4, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 10 }}>+</button>
              </div>}
            </div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.unit}</div><SearchSelect value={f.unit} onChange={v => set("unit", v)} options={UNITS.map(function(u) { return { value: u, label: t[unitKeys[u]] }; })} /></div>
          </div>
          <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.appliesTo}</div><SearchSelect value={f.appliesTo} onChange={v => set("appliesTo", v)} options={APPLIES.map(function(a) { return { value: a, label: t[appKeys[a]] }; })} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.markupRate} <span style={{ fontSize: 9 }}>({t.markupSub})</span></div><input type="number" min="0" step="0.01" value={f.markupRate} onChange={function(e) { set("markupRate", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.costRate} <span style={{ fontSize: 9 }}>({t.costSub})</span></div><input type="number" min="0" step="0.01" value={f.costRate} onChange={function(e) { set("costRate", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></div>
            <div><div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{t.marginOnFee}</div><div style={{ padding: "8px 10px", borderRadius: 5, background: "var(--color-bg)", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700, textAlign: "center", color: margin > 0 ? "#059669" : margin < 0 ? "#DC2626" : "var(--color-text-muted)" }}>{margin >= 0 ? "+" : ""}{f.unit === "percent" ? margin.toFixed(1) + "%" : fmtMoney(margin)}</div></div>
          </div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between" }}>
          <div>{!isNew && !showDel && <button onClick={function() { setShowDel(true); }} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.delete}</button>}{showDel && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ fontSize: 11, color: "#ef4444" }}>{t.confirmDel}</span><button onClick={function() { onDelete(fee.id); }} style={{ padding: "4px 10px", borderRadius: 4, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 11 }}>{t.yes}</button><button onClick={function() { setShowDel(false); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11 }}>{t.no}</button></div>}</div>
          <div style={{ display: "flex", gap: 8 }}><button onClick={onCancel} style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.cancel}</button><button onClick={handleSave} style={{ padding: "7px 20px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.save}</button></div>
        </div>
      </div>
    </div>
  );
}

/* 2026-09-28 (Arturo: "necesito yo mismo sin codigo poder crear o
   eliminar una categoria") — dedicated management screen, separate
   from the buried "+ Add category" inline in FeeForm (which still
   works, and calls the same onAddCategory). Built-in categories are
   shown locked (no delete — they're wired into translations/labels
   throughout the app, e.g. the Financing/operating-fee split on
   Bills). Deleting a custom category that's still assigned to any
   fee reassigns those fees to "other" first, so a fee never ends up
   pointing at a category that no longer exists. */
function CategoryManagerModal({ fees, customCats, onAddCategory, onDeleteCategory, t, onClose }) {
  var catKeys = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", financing: "catFinancing", certification: "catCertification", other: "catOther" };
  var [newCat, setNewCat] = useState("");
  var [err, setErr] = useState("");
  var [confirmDel, setConfirmDel] = useState(null);

  var usageCount = function(name) { return fees.filter(function(f) { return f.category === name; }).length; };

  var handleAdd = async function() {
    var v = newCat.trim();
    if (!v) return;
    var exists = CATEGORIES.includes(v) || customCats.some(function(c) { return c.name.toLowerCase() === v.toLowerCase(); });
    if (exists) { setErr(t.categoryExists); return; }
    setErr("");
    await onAddCategory(v);
    setNewCat("");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }} onClick={onClose}>
      <div onClick={function(e) { e.stopPropagation(); }} style={{ width: 440, maxWidth: "95vw", maxHeight: "85vh", display: "flex", flexDirection: "column", background: "var(--color-bg-card-solid)", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, fontWeight: 700 }}>{t.manageCategoriesTitle}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--color-text-muted)", cursor: "pointer", fontSize: 16 }}>{"✕"}</button>
        </div>
        <div style={{ padding: "14px 20px", overflowY: "auto", flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.builtIn}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
            {CATEGORIES.map(function(c) {
              return <div key={c} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", borderRadius: 6, background: "var(--color-bg)", fontSize: 12 }}>
                <span>{t[catKeys[c]] || c}</span>
                <span style={{ fontSize: 14 }} title={t.builtIn}>{"🔒"}</span>
              </div>;
            })}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.custom}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
            {customCats.length === 0 && <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", padding: "4px 2px" }}>{t.noCustomCategories}</div>}
            {customCats.map(function(c) {
              var n = usageCount(c.name);
              return <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", borderRadius: 6, background: "var(--color-bg)", fontSize: 12 }}>
                <span>{c.name}{n > 0 && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--color-text-muted)" }}>({n})</span>}</span>
                {confirmDel === c.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {n > 0 && <span style={{ fontSize: 10, color: "#ef4444", maxWidth: 140 }}>{t.categoryInUse.replace("{n}", n)}</span>}
                    <button onClick={function() { onDeleteCategory(c); setConfirmDel(null); }} style={{ padding: "3px 8px", borderRadius: 4, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 10 }}>{t.yes}</button>
                    <button onClick={function() { setConfirmDel(null); }} style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 10 }}>{t.no}</button>
                  </div>
                ) : (
                  <button onClick={function() { setConfirmDel(c.id); }} title={t.confirmDeleteCategory} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 12 }}>{"✕"}</button>
                )}
              </div>;
            })}
          </div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--color-border)", display: "flex", gap: 6 }}>
          <input value={newCat} onChange={function(e) { setNewCat(e.target.value); setErr(""); }} placeholder={t.newCategoryPlaceholder} style={{ ...inp, ...(err ? { borderColor: "#ef4444" } : {}) }} onKeyDown={function(e) { if (e.key === "Enter") handleAdd(); }} />
          <button onClick={handleAdd} style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>+ {t.addBtn}</button>
        </div>
        {err && <div style={{ padding: "0 20px 10px", fontSize: 11, color: "#ef4444" }}>{err}</div>}
      </div>
    </div>
  );
}

export default function FeeCatalog({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [fees, setFees] = useState([]);
  var [search, setSearch] = useState(""); var [filterCat, setFilterCat] = useState(""); var [showInactive, setShowInactive] = useState(false);
  var [editing, setEditing] = useState(null); var [showForm, setShowForm] = useState(false);
  var [toast, setToast] = useState(null); var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null); var [saving, setSaving] = useState(false);
  var [customCats, setCustomCats] = useState([]);
  var [showCatManager, setShowCatManager] = useState(false);
  var aliveRef = useRef(true);

  var catKeys = { customs: "catCustoms", warehouse: "catWarehouse", qc: "catQC", shipping: "catShipping", financing: "catFinancing", certification: "catCertification", other: "catOther" };
  var unitKeys = { pallet: "unitPallet", case: "unitCase", flat: "unitFlat", percent: "unitPercent" };

  /* gfi-fee-types is a legacy cache that Bills/Invoices read for the
     fee-type dropdown. We keep syncing it from the Supabase catalog
     until those modules are converted in paso 3c. */
  function syncFeeTypes(catalog) {
    var names = (catalog || []).filter(function(f) { return f.isActive !== false; }).map(function(f) { return f.name; });
    try { localStorage.setItem("gfi-fee-types", JSON.stringify(names)); } catch (e) {}
  }

  var reload = function () {
    setError(null); setLoading(true);
    loadFromSupabase("fee_catalog", { force: true })
      .then(function (rows) {
        if (!aliveRef.current) return;
        /* First-time install: seed Supabase with DEFAULT_FEES so the
           catalog isn't empty. After that re-runs are no-ops. */
        if (rows.length === 0) {
          Promise.all(DEFAULT_FEES.map(function (f) { return saveToSupabase("fee_catalog", f); }))
            .then(function (saved) { if (aliveRef.current) { setFees(saved); syncFeeTypes(saved); } })
            .catch(function (e) { if (aliveRef.current) setError("Could not seed default fees: " + e.message); });
        } else {
          setFees(rows); syncFeeTypes(rows);
        }
      })
      .catch(function (e) { if (aliveRef.current) setError("Could not load fee catalog: " + e.message); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
  };
  useEffect(function() {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("fee_catalog")
      .then(function (rows) {
        if (!aliveRef.current) return;
        if (rows.length === 0) {
          Promise.all(DEFAULT_FEES.map(function (f) { return saveToSupabase("fee_catalog", f); }))
            .then(function (saved) { if (aliveRef.current) { setFees(saved); syncFeeTypes(saved); } })
            .catch(function (e) { if (aliveRef.current) setError("Could not seed default fees: " + e.message); });
        } else {
          setFees(rows); syncFeeTypes(rows);
        }
      })
      .catch(function (e) { if (aliveRef.current) setError("Could not load fee catalog: " + e.message); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    /* Custom categories — one-time bootstrap from the old
       localStorage-only key (see loadLegacyLocalCats above) if
       Supabase doesn't have any yet, then Supabase is the only
       source of truth from here on. */
    loadFromSupabase("fee_categories")
      .then(function (rows) {
        if (!aliveRef.current) return;
        if (rows.length === 0) {
          var legacy = loadLegacyLocalCats();
          if (legacy.length > 0) {
            Promise.all(legacy.map(function (name) { return saveToSupabase("fee_categories", { id: String(Date.now()) + Math.random().toString(36).slice(2, 6), name: name }); }))
              .then(function (saved) { if (aliveRef.current) setCustomCats(saved); })
              .catch(function () {});
            return;
          }
        }
        setCustomCats(rows);
      })
      .catch(function () {});
    return function () { aliveRef.current = false; };
  }, []);

  var handleSave = async function(f) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      var saved = await saveToSupabase("fee_catalog", f);
      var ex = fees.find(function(x) { return x.id === f.id; });
      var next = ex ? fees.map(function(x) { return x.id === f.id ? saved : x; }) : fees.concat([saved]);
      setFees(next); syncFeeTypes(next);
      setShowForm(false); setEditing(null); setToast(t.saved);
    } catch (e) { setError("Could not save fee: " + e.message); setToast("\u26a0 " + e.message); }
    finally { setSaving(false); }
  };
  var handleDelete = async function(id) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await deleteFromSupabase("fee_catalog", id);
      var next = fees.filter(function(x) { return x.id !== id; });
      setFees(next); syncFeeTypes(next);
      setShowForm(false); setEditing(null); setToast(t.deleted);
    } catch (e) { setError("Could not delete fee: " + e.message); setToast("\u26a0 " + e.message); }
    finally { setSaving(false); }
  };

  var addCategory = async function(name) {
    try {
      var saved = await saveToSupabase("fee_categories", { id: String(Date.now()) + Math.random().toString(36).slice(2, 6), name: name });
      setCustomCats(function(prev) { return prev.concat([saved]); });
    } catch (e) { setToast("\u26a0 " + e.message); }
  };
  /* Any fee still assigned to the category being deleted is moved to
     "other" first \u2014 a category is just a label, the fee record itself
     (rates, applies-to, etc.) is never touched beyond that one field. */
  var deleteCategory = async function(cat) {
    try {
      var affected = fees.filter(function(f) { return f.category === cat.name; });
      if (affected.length > 0) {
        var updated = await Promise.all(affected.map(function(f) { return saveToSupabase("fee_catalog", { ...f, category: "other" }); }));
        var byId = {}; updated.forEach(function(u) { byId[u.id] = u; });
        setFees(function(prev) { return prev.map(function(f) { return byId[f.id] || f; }); });
      }
      await deleteFromSupabase("fee_categories", cat.id);
      setCustomCats(function(prev) { return prev.filter(function(c) { return c.id !== cat.id; }); });
    } catch (e) { setToast("\u26a0 " + e.message); }
  };

  var filtered = useMemo(function() {
    var list = fees;
    if (!showInactive) list = list.filter(function(f) { return f.isActive !== false; });
    if (search) { var s = search.toLowerCase(); list = list.filter(function(f) { return (f.name || "").toLowerCase().includes(s); }); }
    if (filterCat) list = list.filter(function(f) { return f.category === filterCat; });
    return list;
  }, [fees, search, filterCat, showInactive]);

  /* Filter dropdown offers every custom category that exists (from
     fee_categories), not just ones already assigned to a fee — plus
     any category name still on a fee but somehow not in that list
     (belt-and-suspenders against a stale read). */
  var filterCats = useMemo(function() {
    var used = Array.from(new Set(fees.map(function(f) { return f.category; }).filter(Boolean)));
    var custom = customCats.map(function(c) { return c.name; });
    var all = Array.from(new Set(custom.concat(used)));
    return CATEGORIES.concat(all.filter(function(c) { return !CATEGORIES.includes(c); }));
  }, [fees, customCats]);

  if (loading) return null;
  var th = { padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)", whiteSpace: "nowrap" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div><h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{t.title}</h2><span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{t.subtitle}</span></div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={function() { setShowCatManager(true); }} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{t.manageCategories}</button>
          <button onClick={function() { setEditing(null); setShowForm(true); }} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ {t.add}</button>
        </div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 12, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span><button onClick={reload} style={{ padding: "5px 12px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#B91C1C", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Retry</button></div>}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 220 }} />
        <SearchSelect value={filterCat} onChange={setFilterCat} placeholder={t.allCategories} style={{ minWidth: 150 }} options={filterCats.map(function(c) { return { value: c, label: t[catKeys[c]] || c }; })} />
        <label style={{ fontSize: 11, color: "var(--color-text-muted)", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}><input type="checkbox" checked={showInactive} onChange={function(e) { setShowInactive(e.target.checked); }} />{t.showInactive}</label>
      </div>
      <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid var(--color-border)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>{t.name}</th>
            <th style={th}>{t.category}</th>
            <th style={th}>{t.unit}</th>
            <th style={{ ...th, textAlign: "right" }}>{t.markupRate}</th>
            <th style={{ ...th, textAlign: "right" }}>{t.costRate}</th>
            <th style={{ ...th, textAlign: "right" }}>{t.marginOnFee}</th>
            <th style={{ ...th, textAlign: "center" }}>{t.appliesTo}</th>
            <th style={{ ...th, textAlign: "center" }}></th>
          </tr></thead>
          <tbody>{filtered.map(function(f) {
            var margin = (parseFloat(f.markupRate) || 0) - (parseFloat(f.costRate) || 0);
            return <tr key={f.id} onClick={function() { setEditing(f); setShowForm(true); }} style={{ cursor: "pointer", borderBottom: "1px solid var(--color-border)", opacity: f.isActive === false ? 0.5 : 1 }} onMouseEnter={function(e) { e.currentTarget.style.background = "var(--color-bg-hover)"; }} onMouseLeave={function(e) { e.currentTarget.style.background = "transparent"; }}>
              <td style={{ padding: "9px 8px", fontSize: 12, fontWeight: 600 }}>{f.name}</td>
              <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{t[catKeys[f.category]] || f.category}</td>
              <td style={{ padding: "9px 8px", fontSize: 11, color: "var(--color-text-secondary)" }}>{t[unitKeys[f.unit]] || f.unit}</td>
              <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{f.unit === "percent" ? (f.markupRate || 0) + "%" : fmtMoney(f.markupRate)}</td>
              <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--color-text-secondary)" }}>{f.unit === "percent" ? (f.costRate || 0) + "%" : fmtMoney(f.costRate)}</td>
              <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: margin > 0 ? "#059669" : margin < 0 ? "#DC2626" : "var(--color-text-muted)" }}>{margin > 0 ? "+" : ""}{f.unit === "percent" ? margin.toFixed(1) + "%" : fmtMoney(margin)}</td>
              <td style={{ padding: "9px 8px", textAlign: "center", fontSize: 10 }}>{f.appliesTo === "both" ? "Bill + Inv" : f.appliesTo === "bill" ? "Bill" : "Invoice"}</td>
              <td style={{ padding: "9px 4px", textAlign: "center" }} onClick={function(e) { e.stopPropagation(); }}><button onClick={function() { setEditing(f); setShowForm(true); }} style={{ background: "none", border: "none", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.editBtn}</button></td>
            </tr>;
          })}</tbody>
        </table>
      </div>
      {showForm && <FeeForm fee={editing} fees={fees} t={t} lang={lang} customCats={customCats} onAddCategory={addCategory} onSave={handleSave} onDelete={handleDelete} onCancel={function() { setShowForm(false); setEditing(null); }} />}
      {showCatManager && <CategoryManagerModal fees={fees} customCats={customCats} onAddCategory={addCategory} onDeleteCategory={deleteCategory} t={t} onClose={function() { setShowCatManager(false); }} />}
      {toast && <Toast message={toast} onClose={function() { setToast(null); }} />}
    </div>
  );
}